/**
 * Copyright (c) 2024 TechDivision GmbH
 * All rights reserved
 *
 * This product includes proprietary software developed at TechDivision GmbH, Germany
 * For more information see https://www.techdivision.com/
 *
 * To obtain a valid license for using this software please contact us at
 * license@techdivision.com
 *
 * @author Simon Paidla <s.paidla@techdivision.com>
 */

import React from 'react';
import {
  Grid, Table, TableBody, TableCell, TableContainer, TableRow, Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { previewData } from '../../../../../Signals/formStateSignal';

/**
 * AdditionalCostsPreview Component
 * @returns {JSX.Element}
 * @constructor
 */
export function AdditionalCostsPreview() {
  const { t } = useTranslation('translations');

  const {
    flatFeePreviews,
    preUseCharge,
  } = previewData.value;

  const renderHeader = () => (
    <Typography
      sx={{
        fontWeight: 'bold',
        letterSpacing: '1px',
        textTransform: 'uppercase',
        color: '#49ad33',
      }}
    >
      {t('additionalCostsPreviewHeader')}
    </Typography>
  );

  const renderPreUseCharge = () => {
    if (!preUseCharge) {
      return null;
    }

    return (
      <TableRow
        key="preUseCharge"
        sx={{
          backgroundColor: flatFeePreviews.length % 2 === 0 ? 'rgba(73,173,51,0.1)' : 'rgba(73,173,51,0.2)',
        }}
      >
        <TableCell
          sx={{
            fontSize: '1rem',
            p: '12px',
            border: 'none',
          }}
          align="left"
        >
          {t('nonRecurring')}
        </TableCell>
        <TableCell
          sx={{
            fontSize: '1rem',
            p: '12px',
            border: 'none',
          }}
          align="left"
        >
          Vorabnutzungs Kosten
        </TableCell>
        <TableCell
          sx={{
            fontSize: '1rem',
            p: '12px',
            border: 'none',
          }}
          align="right"
        >
          {preUseCharge}
          €
        </TableCell>
      </TableRow>
    );
  };

  if (flatFeePreviews.length < 0 && !preUseCharge) {
    return null;
  }

  return (
    <Grid container mt={4}>
      <Grid item xs={12}>
        {renderHeader()}
        <TableContainer sx={{ mt: 2 }}>
          <Table>
            <TableBody>
              {flatFeePreviews.map((fee, index) => (
                <TableRow
                  key={fee.identifier}
                  sx={{
                    backgroundColor: index % 2 === 0 ? 'rgba(73,173,51,0.1)' : 'rgba(73,173,51,0.2)',
                  }}
                >
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      p: '12px',
                      border: 'none',
                    }}
                    align="left"
                  >
                    {fee.paymentFrequency ?? t('nonRecurring')}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      p: '12px',
                      border: 'none',
                    }}
                    align="left"
                  >
                    {fee.name}
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: '1rem',
                      p: '12px',
                      border: 'none',
                    }}
                    align="right"
                  >
                    {fee.price.toFixed(2)}
                    €
                  </TableCell>
                </TableRow>
              ))}
              {renderPreUseCharge()}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}
